import {ChangeDetectorRef, Component, OnInit, ViewContainerRef} from '@angular/core';
import {ConfigurationService} from '../configuration.service';
import {NotificationPopupService} from '../../shared/notification-popup/services/notification-popup.service';

@Component({
    selector: 'app-optimization-schedule',
    templateUrl: './optimization-schedule.component.html',
    styleUrls: ['./optimization-schedule.component.scss']
})
export class OptimizationScheduleComponent implements OnInit {
    // Days prior to optimize on the overnight run. Each option is {value, label}: value is
    // the integer "days in the future" stored in the database; label is that day rendered
    // as a YYYY-MM-DD calendar date for display. optimizationScheduleDaysAll holds every
    // selectable option; optimizationScheduleDays is the (possibly filtered) view bound to
    // the multiselect; optimizationScheduleSelectedDays holds the enabled integer day
    // values (valuePrimitive keeps the model as plain integers).
    public optimizationScheduleDaysAll: {value: number; label: string}[] = [];
    public optimizationScheduleDays: {value: number; label: string}[] = [];
    public optimizationScheduleSelectedDays: number[] = [];

    // Baseline days-prior schedule (rail.parameters), used as the fallback count
    // when no override is currently selected.
    public daysPriorScheduleBaseline: number[] = [];

    // Guards the section from rendering before the saved config (and its baseline
    // schedule) has finished loading, so nothing shows an empty/default selection that
    // isn't actually this tenant's saved override.
    public configLoaded = false;

    public saving = false;

    constructor(private configurationSvc: ConfigurationService,
                private notificationPopupSvc: NotificationPopupService,
                private viewContainerRef: ViewContainerRef,
                private cdr: ChangeDetectorRef) {
    }

    ngOnInit(): void {
        this.optimizationScheduleDaysAll = this.buildOptimizationScheduleOptions(180);
        this.optimizationScheduleDays = this.optimizationScheduleDaysAll;
        this.loadSavedConfiguration();
    }

    // Builds the selectable options 1..maxDay. value is the integer days-in-the-future
    // (what gets stored); label is today + value days, formatted YYYY-MM-DD for display.
    private buildOptimizationScheduleOptions(maxDay: number): {value: number; label: string}[] {
        const base = new Date();
        base.setHours(0, 0, 0, 0);
        const options: {value: number; label: string}[] = [];
        for (let i = 1; i <= maxDay; i++) {
            const date = new Date(base);
            date.setDate(date.getDate() + i);
            options.push({value: i, label: this.formatDate(date)});
        }
        return options;
    }

    private formatDate(date: Date): string {
        const year = date.getFullYear();
        const month = `${date.getMonth() + 1}`.padStart(2, '0');
        const day = `${date.getDate()}`.padStart(2, '0');
        return `${year}-${month}-${day}`;
    }

    // Prepopulates the screen from the previously saved optimization_schedule set, and the
    // read-only days_prior_schedule baseline. Applied defensively so missing/partial
    // config falls back to defaults.
    private loadSavedConfiguration(): void {
        this.configurationSvc.getConfiguration().subscribe(config => {
            // Bound the selectable range to the max day in the days_prior_schedule
            // parameter; fall back to the default range if it is unavailable.
            const daysPriorSchedule = config?.days_prior_schedule;
            if (Array.isArray(daysPriorSchedule) && daysPriorSchedule.length) {
                this.daysPriorScheduleBaseline = daysPriorSchedule;
                const maxDay = Math.max(...daysPriorSchedule);
                this.optimizationScheduleDaysAll = this.buildOptimizationScheduleOptions(maxDay);
                this.optimizationScheduleDays = this.optimizationScheduleDaysAll;
            }

            const optimizationSchedule = config?.optimization_schedule;
            if (optimizationSchedule?.days) {
                this.optimizationScheduleSelectedDays = optimizationSchedule.days;
            }

            this.configLoaded = true;
            this.cdr.detectChanges();
        }, error => {
            console.error(`Error retrieving saved configuration ${error}`);
            this.configLoaded = true;
            this.cdr.detectChanges();
        });
    }

    public handleFilterOptimizationDays(value: string): void {
        const term = value ? value.trim().toLowerCase() : '';
        this.optimizationScheduleDays = term.length
            ? this.optimizationScheduleDaysAll.filter(o =>
                o.label.toLowerCase().includes(term) || o.value.toString().startsWith(term))
            : this.optimizationScheduleDaysAll;
        this.cdr.detectChanges();
    }

    // Total nights that will be optimized: the override list if the user has
    // selected one, otherwise the baseline days-prior schedule. Mirrors the
    // override-or-baseline resolution in rail_sora_app's get_days_prior_schedule().
    public get totalOptimizationDays(): number {
        const selectedCount = this.optimizationScheduleSelectedDays?.length ?? 0;
        return selectedCount ? selectedCount : (this.daysPriorScheduleBaseline?.length ?? 0);
    }

    public onSave(): void {
        const payload = {
            optimization_schedule: {
                days: this.optimizationScheduleSelectedDays
            }
        };

        this.saving = true;
        this.configurationSvc.saveConfiguration(payload).subscribe(() => {
            this.notificationPopupSvc.displayPageNotification(this.viewContainerRef, {
                notificationType: 'success',
                message: 'Configuration saved.'
            });
            this.saving = false;
        }, error => {
            console.error(`Error saving configuration ${error}`);
            this.notificationPopupSvc.displayPageNotification(this.viewContainerRef, {
                notificationType: 'error',
                message: 'Failed to save configuration.'
            });
            this.saving = false;
        });
    }
}
